import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { timeEntryEndpoints } from '@/lib/api/endpoints';
import { timeEntryKeys, timesheetKeys } from '@/lib/queryKeys';
import type { TimesheetDayEntry } from '@/types/timesheet';

/**
 * Close an open session from the office, at the current time.
 *
 * Same route the job page uses, but that hook only refreshes one appointment's
 * caches. Closing from the timesheet has to move the period grid, the day
 * listing and the on-the-clock list together, and the job page's copy too in
 * case it is open behind this one.
 */
export function useClockOutTimesheetEntry() {
  const queryClient = useQueryClient();
  return useApiMutation<TimesheetDayEntry, { entryId: string; technicianName?: string }>({
    mutationFn: ({ entryId }) =>
      apiPost<TimesheetDayEntry, Record<string, never>>(
        operationsClient,
        timeEntryEndpoints.clockOut(entryId),
        {},
      ),
    onSuccess: (_entry, { technicianName }) => {
      void queryClient.invalidateQueries({ queryKey: timesheetKeys.all() });
      void queryClient.invalidateQueries({ queryKey: timeEntryKeys.all() });
      toast.success(technicianName ? `${technicianName} clocked out` : 'Clocked out');
    },
  });
}
